<template>
  <Teleport to="body">
    <Transition name="app-alert">
      <div v-if="current" class="app-alert__backdrop" role="presentation" @click.self="close">
        <section
          class="app-alert"
          :class="`app-alert--${current.tone || 'info'}`"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="app-alert-title"
          aria-describedby="app-alert-message"
        >
          <div class="app-alert__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 8.2v4.6M12 16.25h.01" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" />
              <circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.8" />
            </svg>
          </div>
          <h2 id="app-alert-title" class="app-alert__title">{{ current.title || defaultTitle }}</h2>
          <p id="app-alert-message" class="app-alert__message">{{ current.message }}</p>
          <button ref="button" class="app-alert__button" type="button" @click="close">
            {{ current.buttonText || 'Понятно' }}
          </button>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import type { AppAlertDetail } from "@/utils/appAlert";

const current = ref<AppAlertDetail | null>(null);
const queue: AppAlertDetail[] = [];
const button = ref<HTMLButtonElement | null>(null);
const defaultTitle = computed(() => current.value?.tone === "error" ? "Что-то пошло не так" : "Обрати внимание");

function showNext() {
  current.value = queue.shift() || null;
  if (current.value) nextTick(() => button.value?.focus());
}

function handleAlert(event: Event) {
  const detail = (event as CustomEvent<AppAlertDetail>).detail;
  if (!detail?.message) return;
  queue.push(detail);
  if (!current.value) showNext();
}

function close() {
  current.value = null;
  window.setTimeout(showNext, 180);
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === "Escape" && current.value) close();
}

onMounted(() => {
  window.addEventListener("app:alert", handleAlert);
  document.addEventListener("keydown", handleKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener("app:alert", handleAlert);
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.app-alert__backdrop {
  position: fixed;
  inset: 0;
  z-index: 20000;
  display: grid;
  place-items: center;
  box-sizing: border-box;
  padding: max(20px, env(safe-area-inset-top)) 20px max(20px, env(safe-area-inset-bottom));
  background: rgba(9, 10, 14, 0.66);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.app-alert {
  width: min(100%, 380px);
  box-sizing: border-box;
  padding: 26px 24px 22px;
  text-align: center;
  color: #17181d;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 26px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.3);
}

.app-alert__icon {
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  margin: 0 auto 17px;
  color: #fff;
  background: linear-gradient(135deg, #ff4458, #ff795f);
  border-radius: 18px;
  box-shadow: 0 10px 24px rgba(255, 68, 88, 0.3);
}

.app-alert__icon svg { width: 28px; height: 28px; }
.app-alert--error .app-alert__icon { background: linear-gradient(135deg, #f04455, #ff765d); }
.app-alert__title { margin: 0; font-size: 21px; line-height: 1.2; font-weight: 700; }
.app-alert__message { margin: 10px 0 22px; color: #62656d; font-size: 15px; line-height: 1.48; white-space: pre-line; }
.app-alert__button {
  width: 100%; min-height: 50px; padding: 12px 20px; border: 0; border-radius: 16px;
  color: #fff; background: linear-gradient(90deg, #ff4458, #ff795f); font: inherit; font-size: 16px; font-weight: 700;
  cursor: pointer; box-shadow: 0 8px 20px rgba(255, 68, 88, 0.24); -webkit-tap-highlight-color: transparent;
}
.app-alert__button:active { transform: scale(0.985); }
.app-alert__button:focus-visible { outline: 3px solid rgba(255, 68, 88, 0.3); outline-offset: 3px; }

.app-alert-enter-active, .app-alert-leave-active { transition: opacity 180ms ease; }
.app-alert-enter-active .app-alert, .app-alert-leave-active .app-alert { transition: transform 220ms cubic-bezier(.22,1,.36,1), opacity 180ms ease; }
.app-alert-enter-from, .app-alert-leave-to { opacity: 0; }
.app-alert-enter-from .app-alert, .app-alert-leave-to .app-alert { opacity: 0; transform: translateY(12px) scale(.96); }

:global(html.dark) .app-alert {
  color: #f7f7f8; background: rgba(27, 28, 34, 0.98); border-color: rgba(255, 255, 255, 0.09);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.56);
}
:global(html.dark) .app-alert__message { color: #b8bac2; }

@media (prefers-reduced-motion: reduce) {
  .app-alert-enter-active, .app-alert-leave-active, .app-alert-enter-active .app-alert, .app-alert-leave-active .app-alert { transition: none; }
}
</style>
